import { useContext, useEffect, useState } from 'react';
import {
  generatePath,
  useHistory,
  useParams,
  useRouteMatch
} from 'react-router-dom';
import { TabList, Tab, TabPanel, Tabs, TabPanelBody } from 'components/tabs';
import { FormattedMessage } from 'react-intl';
import {
  Panel,
  PanelBody,
  PanelHeader,
  PanelSubtitle,
  PanelTitle,
  PanelTitleButton,
  PanelTitleWrapper
} from 'components/panel';
import messages from 'messages';
import Breadcrumbs from 'components/breadcrumbs/Breadcrumbs';
import Breadcrumb from 'components/breadcrumbs/Breadcrumb';
import {
  ROUTE_EXERCISES_PROGRAMS_ENTITY_PROGRAM_EXERCISES_EXERCISE,
  ROUTE_USER_ENTITY_PROGRAMS,
  ROUTE_USER_ENTITY_PROGRAMS_PROGRAM
} from 'routes/RouteList';
import { StoreContext } from 'index';
import RetryPanel from 'containers/partials/error-boundary/RetryPanel';
import { MenuWrapper } from 'components/menu';
import RemoveExerciseButton from 'containers/partials/buttons/RemoveExerciseButton';
import Loader from 'components/loader/Loader';
import { Button } from 'components/button';
import Icon from 'components/icon/Icon';
import useExercise from 'hooks/queries/useExercise';
import { useStore } from 'stores/Store';
import ExerciseCard from 'containers/partials/cards/ExerciseCard';
import ExerciseNavigatorButtons from 'containers/pages/person/programs/components/ExerciseNavigatorButtons';
import { isMzhUserAgent } from 'utils/browser';

const ProgramExercise = ({ tabIndex }) => {
  const {
    authStore: { user }
  } = useContext(StoreContext);
  const { entityId, exerciseId, programId } = useParams();
  const { push } = useHistory();

  const program = useStore(state => state.program);
  const { loading, error, fetchExercise } = useExercise(entityId);
  const [exercise, setExercise] = useState(null);

  const active = useRouteMatch([
    ROUTE_EXERCISES_PROGRAMS_ENTITY_PROGRAM_EXERCISES_EXERCISE
  ]);

  useEffect(() => {
    const getExercise = async () => {
      const exercise = await fetchExercise(exerciseId);
      setExercise(exercise);
    };
    getExercise();
  }, [exerciseId]);

  if (loading || !exercise)
    return (
      <Panel active={active?.isExact}>
        <Loader />
      </Panel>
    );
  if (error) return <RetryPanel />;

  return (
    <Panel active={active?.isExact}>
      <PanelHeader>
        <Breadcrumbs>
          <Breadcrumb
            to={generatePath(ROUTE_USER_ENTITY_PROGRAMS, {
              entityId,
              programId
            })}
          >
            <FormattedMessage {...messages.breadcrumbPrograms} />
          </Breadcrumb>
          <Breadcrumb
            to={generatePath(ROUTE_USER_ENTITY_PROGRAMS_PROGRAM, {
              entityId,
              programId
            })}
          >
            <FormattedMessage {...messages.breadcrumbProgram} />
          </Breadcrumb>
        </Breadcrumbs>

        {isMzhUserAgent() && (
          <div
            className="c-panel__title shown-at-tiny"
            style={{ width: '100%' }}
          >
            <Button
              tiny
              onClick={() =>
                push(
                  generatePath(ROUTE_USER_ENTITY_PROGRAMS_PROGRAM, {
                    entityId,
                    programId
                  })
                )
              }
            >
              <Icon mediumIcon id="arrow-right" mirrored />
              <span className="u-margin-right-small">Terug naar overzicht</span>
            </Button>
          </div>
        )}

        <PanelTitleWrapper>
          <PanelTitle>
            {isMzhUserAgent() && (
              <Button
                extraClassNames="shown-till-tiny"
                tiny
                onClick={() =>
                  push(
                    generatePath(ROUTE_USER_ENTITY_PROGRAMS_PROGRAM, {
                      entityId,
                      programId
                    })
                  )
                }
              >
                <Icon mediumIcon id="arrow-right" mirrored />
              </Button>
            )}

            <span title={exercise.title}>{exercise.title}</span>

            {user.canRemoveExercises(entityId) && exercise.editable && (
              <MenuWrapper trigger={<PanelTitleButton />}>
                <RemoveExerciseButton
                  entityId={entityId}
                  exerciseId={exerciseId}
                />
              </MenuWrapper>
            )}
          </PanelTitle>

          <PanelSubtitle>
            <FormattedMessage
              {...messages.exercisesLabelLevelSubtitle}
              values={{ level: exercise.level }}
            />
          </PanelSubtitle>
        </PanelTitleWrapper>
      </PanelHeader>
      <PanelBody withFixedButtonsBottom>
        <Tabs defaultIndex={tabIndex}>
          <TabList>
            <Tab>
              <FormattedMessage {...messages.breadcrumbExercise} />
            </Tab>
          </TabList>
          <TabPanel>
            <TabPanelBody withScroller>
              <ExerciseCard
                entityId={entityId}
                program={program}
                exercise={exercise}
              />
            </TabPanelBody>
          </TabPanel>
        </Tabs>

        <ExerciseNavigatorButtons
          entityId={entityId}
          programId={programId}
          exerciseId={exerciseId}
        />
      </PanelBody>
    </Panel>
  );
};

export default ProgramExercise;
